import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { AppConfig } from '../config/app-config';
import { isStandaloneDisplayMode } from '../analytics/device-context';
import { PwaInstallService } from '../services/pwa-install.service';

const DISMISS_KEY = 'pwa-prompt-dismissed';
const SHOW_DELAY_MS = 1000;

/**
 * PWA install/open prompt — direct port of Consumer's
 * core/components/PWAInstallPrompt.tsx. Always offers both "Open App" (deep
 * link, for an already-installed PWA) and, when the browser fired
 * `beforeinstallprompt`, "Install App". Skips entirely when already running as
 * the installed app (any app display mode), or if dismissed this session.
 *
 * The `beforeinstallprompt` handshake and deep link live in the shared
 * `PwaInstallService` (also used by the `isBrowserBlocked` hard-block screen);
 * this component only owns its own show-delay and per-session dismissal.
 */
@Component({
  selector: 'app-pwa-install-prompt',
  standalone: true,
  template: `
    @if (showPrompt()) {
      <div class="pointer-events-none fixed inset-0 z-50 flex items-end justify-center">
        <div class="animate-fade-in pointer-events-auto absolute inset-0 bg-black/50 backdrop-blur-sm" (click)="dismiss()"></div>

        <div class="animate-slide-up pointer-events-auto relative mx-4 mb-4 w-full max-w-md">
          <div class="overflow-hidden rounded-2xl border border-border-overlay bg-surface shadow-2xl">
            <div class="p-6 pb-4">
              <div class="flex items-start gap-4">
                <div class="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-accent">
                  <svg class="h-8 w-8 text-text-main" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25z" />
                  </svg>
                </div>

                <div class="min-w-0 flex-1">
                  <h3 class="mb-1 text-lg font-bold text-text-main">{{ appName }}</h3>
                  <p class="text-sm leading-relaxed text-text-secondary">
                    Start your {{ appName }} anytime with one click from your desktop - whenever
                    you need it
                  </p>
                </div>

                <button
                  type="button"
                  (click)="dismiss()"
                  aria-label="Close"
                  class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-colors hover:bg-overlay-dark"
                >
                  <svg class="h-5 w-5 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div class="flex flex-col gap-2 px-6 pb-6">
              <button
                type="button"
                (click)="handleOpenApp()"
                class="w-full rounded-xl bg-accent px-4 py-3 text-sm font-semibold text-text-main transition-colors hover:bg-accent-hover"
              >
                Open App
              </button>

              @if (canInstall()) {
                <button
                  type="button"
                  (click)="handleInstall()"
                  class="w-full rounded-xl bg-purple-accent px-4 py-3 text-sm font-semibold text-text-main transition-colors hover:bg-purple-accent/80"
                >
                  Install App
                </button>
              }

              <button
                type="button"
                (click)="dismiss()"
                class="w-full rounded-xl px-4 py-3 text-sm font-semibold text-text-secondary transition-colors hover:text-text-main"
              >
                Continue in Browser
              </button>
            </div>
          </div>
        </div>
      </div>
    }
  `,
})
export class PwaInstallPromptComponent implements OnInit, OnDestroy {
  private readonly pwaInstall = inject(PwaInstallService);

  readonly appName = AppConfig.JIO_CLOUD_PC_APP_NAME;
  readonly showPrompt = signal(false);
  /** Whether the browser offered a programmatic install — owned by the shared service. */
  readonly canInstall = this.pwaInstall.canInstall;

  private showTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    // Never prompt when already running as the installed app (any app display
    // mode — this PWA launches as window-controls-overlay on desktop, not just
    // standalone). Shared with analytics so the two stay in sync.
    if (isStandaloneDisplayMode()) return;
    if (sessionStorage.getItem(DISMISS_KEY)) return;

    // The service (started at bootstrap) owns the beforeinstallprompt listener;
    // here we only schedule this prompt's appearance.
    this.showTimer = setTimeout(() => this.showPrompt.set(true), SHOW_DELAY_MS);
  }

  ngOnDestroy(): void {
    if (this.showTimer) clearTimeout(this.showTimer);
  }

  dismiss(): void {
    this.showPrompt.set(false);
    sessionStorage.setItem(DISMISS_KEY, 'true');
  }

  async handleInstall(): Promise<void> {
    await this.pwaInstall.promptInstall();
    this.dismiss();
  }

  handleOpenApp(): void {
    this.pwaInstall.openApp();
    this.dismiss();
  }
}
